import { useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button } from '@mui/material';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { unique } from '../../utils/collections';
import DataAlertsLoader from '../loaders/DataAlertsLoader';
import { deploymentOnline } from '../../utils/deployments';
import {
  tableSelectionOptions,
  defaultTableOptions,
  defaultTableInitialState,
} from '../util/data-table';
import { PercentageCell, TooltipHeader } from '../util/DataTableCells';

const NEEDS_DEPLOYMENT = 'NEEDS_DEPLOYMENT';
const ONLINE = 'ONLINE';
const OFFLINE = 'OFFLINE';
const ALERT = 'ALERT';

function determineStatus(now, bid, latestHeartbeat, alerts) {
  if (latestHeartbeat === null) return NEEDS_DEPLOYMENT;
  if (!deploymentOnline(now, latestHeartbeat)) return OFFLINE;
  return alerts.filter((a) => (a?.buildings ?? []).includes(bid) && a.state === 'ALERT').length > 0
    ? ALERT
    : ONLINE;
}

function statusColor(status) {
  if (status === NEEDS_DEPLOYMENT) {
    return 'primary'; // Grey for missing deployment
  }
  if (status === ONLINE) {
    return 'success'; // Green color for copacetic
  }
  if (status === ALERT) {
    return 'warning'; // Yellow color for data alert
  }
  return 'error'; // Red color for offline
}

const BuildingsTable = ({
  buildingsById,
  orgsById,
  alerts,
  latestHeartbeatByBid,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  buildingsSelection,
  onRowSelectionChange,
}) => {
  const now = useMemo(() => new Date(), []);

  const columns = useMemo(() => {
    const orgsOptions = Object.values(orgsById).map((o) => ({ label: o.name, value: o.id }));
    const liveHeader = ({ column }) => (
      <TooltipHeader
        tooltip="Live points have been updated in the past hour. Updated hourly."
        content={column.columnDef.header}
      />
    );
    const liveHeaderPercent = ({ column }) => (
      <TooltipHeader
        tooltip="Updated hourly. Values are in percentiles"
        content={column.columnDef.header}
      />
    );
    const lastIngestTime = ({ column }) => (
      <TooltipHeader
        tooltip="Updated hourly based on the most recent point update in the building"
        content={column.columnDef.header}
      />
    );
    return [
      // TODO: group shared column defs in shared file
      {
        id: 'o_name',
        accessorFn: (b) => [b.orgId],
        header: 'Organization Name',
        filterVariant: 'multi-select',
        filterSelectOptions: orgsOptions,
        Cell: ({ renderedCellValue }) => orgsById[renderedCellValue]?.name ?? renderedCellValue,
      },
      { id: 'b_name', accessorKey: 'name', header: 'Building Name', filterVariant: 'autocomplete' },
      {
        id: 'b_equipCount',
        accessorKey: 'equipCount',
        header: 'Equipment (count)',
        filterVariant: 'range',
      },
      {
        id: 'b_livePointCount',
        header: 'Live Point (count)',
        Header: liveHeader,
        filterVariant: 'range',
        accessorFn: ({ pointCount, stalePointCount }) => pointCount - stalePointCount,
      },
      {
        id: 'b_pointCount',
        accessorKey: 'pointCount',
        header: 'Total Points (count)',
        filterVariant: 'range',
      },
      {
        id: 'b_livePointPercent',
        filterVariant: 'range',
        header: 'Live Point (percent)',
        Header: liveHeaderPercent,
        // Hacking in -1 for NaNs to get sorting the way we want
        accessorFn: ({ pointCount, stalePointCount }) => {
          const result = (pointCount - stalePointCount) / pointCount;
          return Number.isNaN(result) ? -1 : result * 100;
        },
        Cell: PercentageCell,
      },
      {
        id: 'b_lastIngest',
        header: 'Last Data Ingested',
        Header: lastIngestTime,
        sortingFn: 'datetime',
        enableColumnFilter: false,
        // hijacking the min time to seperate no ingests case from no published points case
        accessorFn: ({ lastPointUpdate, pointCount }) =>
          new Date(lastPointUpdate || (pointCount === 0 ? 0 : 1)),
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ row, renderedCellValue }) => {
          if (row.original.pointCount === 0) {
            return (
              <Link to={`/staging?bid=${row.original.id}`}>
                <Button variant="contained" color="primary">
                  Needs PUBLISHED DATA
                </Button>
              </Link>
            );
          }
          if (renderedCellValue.valueOf() === 1) {
            return 'Never';
          }
          return renderedCellValue.toLocaleString();
        },
      },
      {
        id: 'b_status',
        accessorKey: 'status',
        header: 'Status',
        filterVariant: 'select',
        filterSelectOptions: [ALERT, ONLINE, OFFLINE, NEEDS_DEPLOYMENT],
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ row, renderedCellValue }) =>
          renderedCellValue ? (
            <Link to={`/dashboard?bid=${row.original.id}`}>
              <Button variant="contained" color={statusColor(renderedCellValue)}>
                {renderedCellValue}
              </Button>
            </Link>
          ) : null,
      },
      { id: 'b_address', accessorKey: 'address', header: 'Building Address' },
      { id: 'b_purpose', accessorKey: 'purpose', header: 'Building Purpose' },
      {
        id: 'b_area',
        accessorKey: 'area',
        header: 'Building Area',
        accessorFn: ({ area, areaUnit }) => (area !== null ? `${area} ${areaUnit}` : null),
      },
    ];
  }, [orgsById]);

  // This breaks page changes while the page is rendering
  const data = useMemo(
    () =>
      Object.values(buildingsById).map((b) => ({
        ...b,
        status: determineStatus(now, b.id, latestHeartbeatByBid[b.id], alerts),
      })),
    [now, buildingsById, latestHeartbeatByBid, alerts]
  );
  const showOrgs = unique(Object.values(buildingsById).map((b) => b.orgId)).length > 1;

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const buildingSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const buildingColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    ...tableSelectionOptions,
    // https://onboard-data.slack.com/archives/C01JX53PBJ5/p1780328209112699
    enableGlobalFilter: false,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters: buildingColumnFilters,
        sorting: buildingSorting,
        hiddenColumns: ['b_address', 'b_purpose', 'b_area', showOrgs ? 'o_name' : null],
      }),
    },
    // selection
    onRowSelectionChange,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: buildingSorting,
      columnFilters: buildingColumnFilters,
      rowSelection: buildingsSelection,
      showAlertBanner: true,
    },
  });

  return (
    <div>
      <DataAlertsLoader />
      <MaterialReactTable table={mrt} />
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    entities: {
      dataAlerts: { alerts },
      deployments: { deployments },
    },
  } = state;
  const latestHeartbeatByBid = Object.fromEntries(
    Map.groupBy(
      Object.values(deployments).flatMap((d) =>
        d?.buildings?.map((bid) => [bid, d.last_heartbeat])
      ),
      (e) => e[0]
    )
      .entries()
      .map((entry) => [entry[0], Math.max(0, ...entry[1].map((e) => e[1]))])
  );
  return {
    alerts,
    latestHeartbeatByBid,
  };
};

// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(BuildingsTable);
